import clsx from "clsx";
import { createContext, useContext, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface SelectContextValue {
    value: unknown;
    onSelect: (value: unknown) => void;
    close: () => void;
    disabled?: boolean;
}

export const SelectContext = createContext<SelectContextValue | null>(null);

export interface MenuItemProps {
    value: unknown;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
}

export function MenuItem({ value, children, className, disabled }: MenuItemProps) {
    const context = useContext(SelectContext);
    const isDisabled = Boolean(disabled || context?.disabled);
    const isSelected = context?.value === value;

    const handleClick = () => {
        if (isDisabled) return;
        context?.onSelect(value);
        context?.close();
    };

    const itemClassName = twMerge(
        clsx(
            "w-full text-left px-3 py-2 text-sm text-primary hover:bg-primary/5",
            isSelected && "bg-primary/10",
            isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
            "z-100",
        ),
        className,
    );

    return (
        <button
            type="button"
            role="option"
            aria-selected={isSelected}
            aria-disabled={isDisabled}
            className={itemClassName}
            onClick={handleClick}
            disabled={isDisabled}
        >
            {children}
        </button>
    );
}

MenuItem.displayName = "MenuItem";
